import React from 'react';
import { formatEventDate, formatEventTime, hasFinalScore, isPastEvent } from '../../utils/gameSchedule';
import { cardStatus, checkInAction, checkInMeta, losingSide, predictAction, squadLineText } from '../../utils/gameCards';
import { normalizeMediaUrl } from '../../utils/gameSchedule';
import { CheckIcon, ChevronRight, LockIcon, TeamMark } from './gameBits';

function MetaIcon({ icon }) {
  if (icon === 'check') return <CheckIcon className="games-meta-check" />;
  if (icon === 'lock') return <LockIcon />;
  return null;
}

function Sport({ event }) {
  return event.sport ? <em className="games-sport">{event.sport}</em> : null;
}

function FinalCenter({ event }) {
  const lost = losingSide(event);
  return (
    <span className="games-center">
      <Sport event={event} />
      <span className="games-final-row">
        <strong className={`games-score${lost === 'home' ? ' is-lost' : ''}`}>{event.home_score}</strong>
        <em>Final</em>
        <strong className={`games-score${lost === 'away' ? ' is-lost' : ''}`}>{event.away_score}</strong>
      </span>
    </span>
  );
}

function TimeCenter({ event, sub }) {
  return (
    <span className="games-center">
      <Sport event={event} />
      <strong className="games-time">{formatEventTime(event)}</strong>
      {sub ? <em className="games-sub">{sub}</em> : null}
    </span>
  );
}

// A game as the embed's Games tab draws it: the matchup, a rule, then status or meta, the
// squad winner lines on a finished game, and the action buttons.
export default function GameCard({ event, onOpen, onSquad, onPredict, onCheckIn }) {
  const nowMs = Date.now();
  const past = isPastEvent(event, nowMs);
  const scored = hasFinalScore(event);
  const predict = predictAction(event, nowMs, past);
  const showCheckIn = checkInAction(event, nowMs, past);
  const status = cardStatus(event);
  const meta = checkInMeta(event, nowMs);
  const results = Array.isArray(event.squad_results) ? event.squad_results : [];
  const label = event.home_name && event.opponent_name ? `Open game: ${event.home_name} vs ${event.opponent_name}` : 'Open game';

  const open = (e) => {
    if (e.target.closest('button')) return;
    onOpen(event);
  };

  return (
    <article className="games-card" role="button" tabIndex={0} aria-label={label} aria-haspopup="dialog" onClick={open} onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(event);
      }
    }}>
      <div className="games-match">
        <TeamMark name={event.home_name} logoUrl={event.home_logo} />
        {past && scored ? <FinalCenter event={event} /> : (
          <TimeCenter event={event} sub={past ? '' : [event.location_name, formatEventDate(event)].filter(Boolean).join(' • ')} />
        )}
        <TeamMark name={event.opponent_name} logoUrl={event.opponent_logo} />
      </div>
      <hr className="games-rule" />
      {past ? (
        <div className="games-meta">
          <span>{formatEventDate(event)}</span>
          {event.location_name ? <span>{event.location_name}</span> : null}
          <span className="games-meta-right">
            {scored ? (
              event.already_checked_in ? (<><span>Check-in</span><CheckIcon className="games-meta-check" /></>) : 'Didn’t check in'
            ) : (
              <><span>{meta.label}</span><MetaIcon icon={meta.icon} /></>
            )}
          </span>
        </div>
      ) : (
        <div className="games-status">
          <span className={status.checkIn.done ? 'is-done' : ''}>
            {status.checkIn.label}
            {status.checkIn.done ? <CheckIcon className="games-meta-check" /> : null}
          </span>
          <i aria-hidden="true">•</i>
          <span className={status.prediction.done ? 'is-done' : ''}>
            {status.prediction.label}
            {status.prediction.done ? <CheckIcon className="games-meta-check" /> : null}
          </span>
        </div>
      )}
      {past && scored && results.length ? (
        <div className="games-squad-lines">
          {results.map((result) => {
            const cover = normalizeMediaUrl(result.cover_url);
            return (
              <button type="button" key={String(result.squad_id)} className="games-squad-line" onClick={() => onSquad(event, result.squad_id)}>
                {cover ? <img src={cover} alt="" /> : null}
                <span>{squadLineText(result)}</span>
                <ChevronRight />
              </button>
            );
          })}
        </div>
      ) : null}
      {predict.visible || showCheckIn ? (
        <div className={`games-actions${predict.visible && showCheckIn ? '' : ' is-single'}`}>
          {predict.visible ? (
            <button type="button" className="games-btn is-ghost" onClick={() => onPredict(event)}>
              {predict.label}
            </button>
          ) : null}
          {showCheckIn ? (
            <button type="button" className="games-btn is-primary" onClick={() => onCheckIn(event)}>
              {event.already_checked_in ? 'Checked in' : 'Check in'}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
